<template>
  <div v-if="post">
    <Breadcrumb :items="crumbs" />

    <article class="container article">
      <header class="header">
        <span class="pill">{{ post.category }}</span>
        <h1 class="h-title">{{ post.title }}</h1>
        <p class="meta">{{ ui('by') }} {{ post.author }} · {{ post.date }} · {{ post.readTime }}</p>
        <p v-if="isAr" class="note">{{ ui('language_note') }}</p>
      </header>

      <div v-if="post.image" class="cover">
        <ImageSlot :src="post.image" :alt="post.title" />
      </div>

      <p class="lead excerpt">{{ post.excerpt }}</p>
      <!-- eslint-disable-next-line vue/no-v-html -->
      <div class="prose" v-html="body"></div>

      <PillButton to="/blog" variant="secondary" size="md">{{ ui('all_articles') }}</PillButton>
    </article>

    <CtaBand />
  </div>
</template>

<script setup>
import { computed, ref, watch, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Breadcrumb from '../components/Breadcrumb.vue'
import ImageSlot from '../components/ImageSlot.vue'
import PillButton from '../components/PillButton.vue'
import CtaBand from '../components/CtaBand.vue'
import { useLocale } from '../composables/useLocale'
import { usePageMeta } from '../composables/usePageMeta'
import { blogPosts } from '../data'
import { fetchBlogPost } from '../services/api'

const route = useRoute()
const router = useRouter()
const { isAr, t, ui, to } = useLocale()

const post = computed(() => blogPosts.find((item) => item.slug === route.params.slug))

watchEffect(() => {
  if (!post.value) {
    router.replace(to('/blog'))
  }
})

// Bodies ship in the build-time snapshot, so this renders immediately and
// still works with the CMS offline. The fetch below is only a fallback for a
// post published after the last build.
const fetchedBody = ref(null)
const body = computed(() => post.value?.body || fetchedBody.value || '')

watch(post, async (current) => {
  fetchedBody.value = null

  if (!current || current.body) {
    return
  }

  try {
    const fresh = await fetchBlogPost(current.slug)
    fetchedBody.value = fresh?.body ?? null
  } catch {
    // Leave the body empty rather than showing an error in the article.
  }
}, { immediate: true })

const crumbs = computed(() => [
  { label: ui('home'), to: '/' },
  { label: t('nav.blog'), to: '/blog' },
  { label: post.value?.title }
])

usePageMeta(
  computed(() => post.value && (post.value.seoTitle || `${post.value.title} | ${t('brand')}`)),
  computed(() => post.value && (post.value.seoDescription || post.value.excerpt))
)
</script>

<style scoped>
.article {
  max-width: 820px;
  padding-top: clamp(32px, 6vw, 64px);
  display: flex;
  flex-direction: column;
  gap: 28px;
  align-items: flex-start;
}

.header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
}

.meta {
  font-size: 15px;
  color: var(--tertiary);
}

.cover {
  width: 100%;
  height: clamp(220px, 36vw, 420px);
  border-radius: var(--radius-card);
  overflow: hidden;
}

.excerpt {
  font-size: clamp(19px, 2vw, 23px);
  color: var(--ink);
}
</style>
